import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@istari-frontend/ui";
import { Archive, ArchiveRestore, EllipsisVertical, Pencil, Plus, SearchIcon, Settings2, Share2 } from "lucide-react";
import { useId } from "react";

import type { Conversation, ConversationGroupBy, ConversationStatusFilter } from "~/ai-chat/model/conversation";
import { groupConversationsByRecency, groupConversationsByStatus } from "~/ai-chat/model/conversation-grouping";
import { type ConversationReadMarks, hasUnseenUpdate } from "~/ai-chat/model/conversation-read-marks";
import {
  CONVERSATION_DOT_LABEL,
  conversationDotState,
  ConversationStatusDot,
} from "~/ai-chat/ui/conversation-status-dot";
import { useInlineTitleEdit } from "~/ai-chat/ui/use-inline-title-edit";
import { cn } from "~/utils/cn";

interface ConversationListProps {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, title: string | null) => void;
  onArchive: (id: string, archived: boolean) => void;
  // Shares a conversation already saved to the library; never-saved rows show it disabled.
  onShare?: (id: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: ConversationStatusFilter;
  onStatusFilterChange: (value: ConversationStatusFilter) => void;
  groupBy: ConversationGroupBy;
  onGroupByChange: (value: ConversationGroupBy) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isFetchingMore: boolean;
  isLoading?: boolean;
  // The latest list request failed. With no rows it replaces the empty state so a backend error never reads
  // as "no conversations"; with rows loaded they stay visible and the notice takes the Load more slot.
  loadFailed?: boolean;
  onRetry?: () => void;
  // Conversations streaming right now (live), so their dot shows amber immediately
  // instead of waiting on the list refetch.
  runningIds: Set<string>;
  // When each conversation was last seen; a finished turn newer than its mark is unread.
  readMarks: ConversationReadMarks;
}

export function ConversationList({
  conversations,
  activeId,
  onSelect,
  onNew,
  onRename,
  onArchive,
  onShare,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  groupBy,
  onGroupByChange,
  onLoadMore,
  hasMore,
  isFetchingMore,
  isLoading,
  loadFailed = false,
  onRetry,
  runningIds,
  readMarks,
}: ConversationListProps) {
  const headingId = useId();
  const searchId = useId();
  const groups =
    groupBy === "status"
      ? groupConversationsByStatus(conversations, runningIds)
      : groupBy === "none"
        ? [{ label: "", conversations }]
        : groupConversationsByRecency(conversations, new Date());
  const loadFailedNotice = (
    <p className="text-muted-foreground flex items-center gap-2 px-4 py-3 text-sm">
      Couldn&apos;t load conversations.
      {onRetry ? (
        <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </p>
  );

  return (
    <nav aria-labelledby={headingId} className="bg-sidebar flex h-full flex-col">
      <h2 id={headingId} className="sr-only">
        Conversations
      </h2>
      <div className="border-sidebar-border flex h-11 shrink-0 items-center gap-3 border-b px-3.5">
        <div className="border-input bg-background focus-within:border-ring focus-within:ring-ring/50 flex h-8 min-w-0 flex-1 items-center gap-2 rounded-[10px] border px-2.5 shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]">
          <SearchIcon className="text-muted-foreground size-4 shrink-0" />
          <input
            id={searchId}
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search"
            aria-label="Search conversations"
            className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={onNew} className="shrink-0 gap-1.5 rounded-[10px]">
          <Plus className="size-4" />
          New
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="secondary"
              size="icon-sm"
              aria-label="Filter and group conversations"
              className="shrink-0 rounded-[10px]"
            >
              <Settings2 className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel>Status</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={statusFilter}
              onValueChange={(value) => onStatusFilterChange(value as ConversationStatusFilter)}
            >
              <DropdownMenuRadioItem value="active">Active</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Group by</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={groupBy}
              onValueChange={(value) => onGroupByChange(value as ConversationGroupBy)}
            >
              <DropdownMenuRadioItem value="date">Date</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="status">Status</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="none">None</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground px-4 py-3 text-sm">Loading conversations…</p>
      ) : loadFailed && conversations.length === 0 ? (
        loadFailedNotice
      ) : conversations.length === 0 ? (
        <p className="text-muted-foreground px-4 py-3 text-sm">
          {search.trim().length > 0 ? "No matching conversations." : "No conversations yet."}
        </p>
      ) : (
        <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2">
          {groups.map((group) => (
            <section key={group.label} className="flex flex-col gap-0.5">
              {group.label ? (
                <h3 className="text-muted-foreground px-2 py-1.5 text-xs tracking-wide">{group.label}</h3>
              ) : null}
              <ul className="flex flex-col gap-0.5">
                {group.conversations.map((conversation) => (
                  <ConversationRow
                    key={conversation.id}
                    conversation={conversation}
                    isActive={conversation.id === activeId}
                    isRunning={runningIds.has(conversation.id)}
                    isRead={conversation.id === activeId || !hasUnseenUpdate(readMarks, conversation)}
                    onSelect={onSelect}
                    onRename={onRename}
                    onArchive={onArchive}
                    onShare={onShare}
                  />
                ))}
              </ul>
            </section>
          ))}
          {loadFailed ? (
            loadFailedNotice
          ) : hasMore ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onLoadMore}
              disabled={isFetchingMore}
              className="w-full"
            >
              {isFetchingMore ? "Loading…" : "Load more"}
            </Button>
          ) : null}
        </div>
      )}
    </nav>
  );
}

const UNTITLED_LABEL = "Untitled chat";

interface ConversationRowProps {
  conversation: Conversation;
  isActive: boolean;
  isRunning: boolean;
  isRead: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string | null) => void;
  onArchive: (id: string, archived: boolean) => void;
  onShare?: (id: string) => void;
}

function ConversationRow({
  conversation,
  isActive,
  isRunning,
  isRead,
  onSelect,
  onRename,
  onArchive,
  onShare,
}: ConversationRowProps) {
  const edit = useInlineTitleEdit(conversation.title, (title) => onRename(conversation.id, title));
  const label = conversation.title ?? UNTITLED_LABEL;
  const isArchived = conversation.archivedAt !== null;
  const dotState = conversationDotState(isRunning ? "running" : conversation.status, isRead);
  const dotLabel = CONVERSATION_DOT_LABEL[dotState];

  if (edit.isEditing) {
    return (
      <li>
        <input
          aria-label={`Rename ${label}`}
          autoFocus
          value={edit.draft ?? ""}
          onChange={(event) => edit.setDraft(event.target.value)}
          onBlur={edit.handleBlur}
          onKeyDown={edit.handleKeyDown}
          className={cn(
            "text-foreground w-full rounded-lg bg-transparent px-3 py-1.5 text-sm outline-none",
            "focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          )}
        />
      </li>
    );
  }

  return (
    <li className={cn("group/row relative rounded-lg", isActive && "bg-sidebar-accent")}>
      <button
        type="button"
        aria-current={isActive ? "true" : undefined}
        // aria-label overrides descendant text, so fold the status into the accessible
        // name — the dot is visible but decorative, and the id snippet is just noise for SR.
        aria-label={dotLabel ? `${label}, ${dotLabel}` : label}
        onClick={() => onSelect(conversation.id)}
        className={cn(
          "flex w-full items-center gap-1.5 rounded-lg px-3 py-1.5 pr-9 text-left transition-colors outline-none",
          "hover:bg-sidebar-accent focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          isActive && "font-medium",
        )}
      >
        <ConversationStatusDot state={dotState} />
        <span className="text-foreground min-w-0 flex-1 truncate text-sm">{label}</span>
        <span className="text-muted-foreground shrink-0 font-mono text-xs">{conversation.id.slice(0, 6)}…</span>
      </button>
      <div
        className={cn(
          "absolute top-1/2 right-1 -translate-y-1/2 transition-opacity",
          !isActive && "opacity-0 group-focus-within/row:opacity-100 group-hover/row:opacity-100",
        )}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Options for ${label}`}
              className="hover:bg-accent flex items-center justify-center rounded p-1"
            >
              <EllipsisVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={edit.beginEdit}>
              <Pencil />
              Rename
            </DropdownMenuItem>
            {onShare ? (
              <ShareChatItem
                isSaved={conversation.transcriptResourceId !== null}
                onShare={() => onShare(conversation.id)}
              />
            ) : null}
            <DropdownMenuItem onSelect={() => onArchive(conversation.id, !isArchived)}>
              {isArchived ? <ArchiveRestore /> : <Archive />}
              {isArchived ? "Unarchive" : "Archive"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}

function ShareChatItem({ isSaved, onShare }: { isSaved: boolean; onShare: () => void }) {
  const item = (
    <DropdownMenuItem disabled={!isSaved} onSelect={onShare}>
      <Share2 />
      Share chat
    </DropdownMenuItem>
  );
  if (isSaved) return item;
  // A disabled Radix item drops pointer events, so the wrapper is what receives the hover.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>{item}</div>
      </TooltipTrigger>
      <TooltipContent side="left">Save this chat to share it</TooltipContent>
    </Tooltip>
  );
}
